import MarkdownIt from 'markdown-it';
import imageRule from 'markdown-it/lib/rules_inline/image.mjs';

// Formatting rules for different contexts (channels and special contexts)
// marks: inline formatting (strong, em, code, link, strike)
// nodes: block structures (bulletList, orderedList, codeBlock, blockquote)
export const FORMATTING = {
  // Channel formatting
  'Channel::Email': {
    marks: ['strong', 'em', 'code', 'link'],
    nodes: ['bulletList', 'orderedList', 'codeBlock', 'blockquote', 'image'],
    menu: [
      'copilot',
      'strong',
      'em',
      'code',
      'link',
      'bulletList',
      'orderedList',
      'undo',
      'redo',
    ],
  },
  'Channel::WebWidget': {
    marks: ['strong', 'em', 'code', 'link', 'strike'],
    nodes: ['bulletList', 'orderedList', 'codeBlock', 'blockquote', 'image'],
    menu: [
      'copilot',
      'strong',
      'em',
      'code',
      'link',
      'strike',
      'bulletList',
      'orderedList',
      'undo',
      'redo',
    ],
  },
  'Channel::Api': {
    marks: ['strong', 'em'],
    nodes: [],
    menu: ['copilot', 'strong', 'em', 'undo', 'redo'],
  },
  'Channel::FacebookPage': {
    marks: ['strong', 'em', 'code', 'strike'],
    nodes: ['bulletList', 'orderedList', 'codeBlock'],
    menu: [
      'copilot',
      'strong',
      'em',
      'code',
      'strike',
      'bulletList',
      'orderedList',
      'undo',
      'redo',
    ],
  },
  'Channel::TwitterProfile': {
    marks: [],
    nodes: [],
    menu: [],
  },
  'Channel::TwilioSms': {
    marks: [],
    nodes: [],
    menu: [],
  },
  'Channel::Sms': {
    marks: [],
    nodes: [],
    menu: [],
  },
  'Channel::Whatsapp': {
    marks: ['strong', 'em', 'code', 'strike'],
    nodes: ['bulletList', 'orderedList', 'codeBlock'],
    menu: [
      'copilot',
      'strong',
      'em',
      'code',
      'strike',
      'bulletList',
      'orderedList',
      'undo',
      'redo',
    ],
  },
  'Channel::Line': {
    marks: ['strong', 'em', 'code', 'strike'],
    nodes: ['codeBlock'],
    menu: ['copilot', 'strong', 'em', 'code', 'strike', 'undo', 'redo'],
  },
  'Channel::Telegram': {
    marks: ['strong', 'em', 'link', 'code'],
    nodes: [],
    menu: ['copilot', 'strong', 'em', 'link', 'code', 'undo', 'redo'],
  },
  'Channel::Instagram': {
    marks: ['strong', 'em', 'code', 'strike'],
    nodes: ['bulletList', 'orderedList'],
    menu: [
      'copilot',
      'strong',
      'em',
      'code',
      'bulletList',
      'orderedList',
      'strike',
      'undo',
      'redo',
    ],
  },
  'Channel::Tiktok': {
    marks: [],
    nodes: [],
    menu: [],
  },
  // Special contexts (not actual channels)
  'Context::PrivateNote': {
    marks: ['strong', 'em', 'code', 'link', 'strike'],
    nodes: ['bulletList', 'orderedList', 'codeBlock', 'blockquote'],
    menu: [
      'copilot',
      'strong',
      'em',
      'code',
      'link',
      'strike',
      'bulletList',
      'orderedList',
      'undo',
      'redo',
    ],
  },
  'Context::Default': {
    marks: ['strong', 'em', 'code', 'link', 'strike'],
    nodes: ['bulletList', 'orderedList', 'codeBlock', 'blockquote'],
    menu: [
      'strong',
      'em',
      'code',
      'link',
      'strike',
      'bulletList',
      'orderedList',
      'undo',
      'redo',
    ],
  },
  'Context::MessageSignature': {
    marks: ['strong', 'em', 'link'],
    nodes: ['image'],
    menu: ['strong', 'em', 'link', 'undo', 'redo', 'imageUpload'],
  },
  'Context::InboxSettings': {
    marks: ['strong', 'em', 'link'],
    nodes: [],
    menu: ['strong', 'em', 'link', 'undo', 'redo'],
  },
  'Context::Plain': {
    marks: [],
    nodes: [],
    menu: [],
  },
};

// Editor menu options for Full Editor
export const ARTICLE_EDITOR_MENU_OPTIONS = [
  'strong',
  'em',
  'strike',
  'link',
  'undo',
  'redo',
  'bulletList',
  'orderedList',
  'h1',
  'h2',
  'h3',
  'imageUpload',
  'code',
  'insertTable',
];

const getMarkdownLineOffsets = source => {
  const offsets = [0];
  for (let index = 0; index < source.length; index += 1) {
    if (source[index] === '\n') offsets.push(index + 1);
  }
  return offsets;
};

const normalizeMarkdownLineEndings = source =>
  String(source ?? '').replace(/\r\n?/g, '\n');

const restoreMarkdownLineEndings = (source, original) => {
  const value = String(original ?? '');
  if (value.includes('\r\n')) return source.replace(/\r?\n/g, '\r\n');
  if (value.includes('\r')) return source.replace(/\r?\n/g, '\r');
  return source;
};

const getProtectedMarkdownRanges = (source, tokenizer) => {
  const env = {};
  let tokens = [];

  try {
    tokens = tokenizer.parse(source, env);
  } catch {
    return { references: {}, ranges: [] };
  }

  const lineOffsets = getMarkdownLineOffsets(source);
  const ranges = tokens
    .filter(
      token =>
        (token.type === 'code_block' || token.type === 'fence') && token.map
    )
    .map(token => ({
      start: lineOffsets[token.map[0]] ?? 0,
      end: lineOffsets[token.map[1]] ?? source.length,
    }));

  return { references: env.references || {}, ranges };
};

// Use the same grammar as MessageMarkdownTransformer. The image rule is
// wrapped through MarkdownIt's public Ruler API so we can collect exact source
// spans that the parser actually recognizes; this avoids treating image-like
// text in link destinations or code spans as an image. Importing the rule
// directly keeps the lookup stable in production builds, where minifiers may
// rename functions returned by `ruler.getRules()`.
const imageContextTokenizer = MarkdownIt('commonmark', {
  html: false,
  linkify: false,
});
imageContextTokenizer.enable(['entity', 'escape']);
imageContextTokenizer.disable(['table', 'hr', 'heading', 'lheading'], true);

const findUnresolvedCollapsedImage = (state, start) => {
  // The announcement editor historically stripped collapsed image wrappers
  // even when their reference definition was absent. Keep that compatibility
  // behavior for `![label][]`; a shortcut image (`![label]`) without a
  // definition is deliberately left as plain text.
  if (
    state.src.charCodeAt(start) !== 0x21 ||
    state.src.charCodeAt(start + 1) !== 0x5b
  ) {
    return null;
  }

  const labelEnd = state.md.helpers.parseLinkLabel(state, start + 1, false);
  if (labelEnd < 0 || state.src.slice(labelEnd + 1, labelEnd + 3) !== '[]') {
    return null;
  }

  return {
    start,
    end: labelEnd + 3,
    source: state.src,
    replacement: state.src.slice(start + 2, labelEnd),
  };
};

imageContextTokenizer.inline.ruler.at('image', (state, silent) => {
  const start = state.pos;
  const tokenStart = state.tokens.length;
  const matched = imageRule(state, silent);

  if (silent || !state.env) return matched;

  const imageToken = state.tokens
    .slice(tokenStart)
    .find(token => token.type === 'image');
  let span = null;
  if (imageToken) {
    span = {
      start,
      end: state.pos,
      source: state.src,
      replacement: imageToken.content || '',
    };
  } else if (!matched) {
    span = findUnresolvedCollapsedImage(state, start);
  }
  if (!span) return matched;

  if (!Array.isArray(state.env.unsupportedImageSpans)) {
    state.env.unsupportedImageSpans = [];
  }
  state.env.unsupportedImageSpans.push(span);

  return matched;
});

const maskRanges = (source, ranges) => {
  if (!ranges.length) return source;

  const masked = source.split('');
  ranges.forEach(({ start, end }) => {
    for (let index = start; index < end; index += 1) {
      if (masked[index] !== '\n' && masked[index] !== '\r') {
        masked[index] = ' ';
      }
    }
  });
  return masked.join('');
};

// Reference definitions are block-level metadata and are not inline content.
// Mask their source lines before the global inline pass so image-like text in
// a destination (for example `[ref]: https://host/![x](y)`) is never treated
// as an image candidate.
const maskReferenceDefinitionLines = source =>
  source.replace(/^[ \t]{0,3}\[[^\]\r\n]+\]:[^\r\n]*(?=\r?$)/gm, line =>
    line.replace(/[^\r\n]/g, ' ')
  );

const collectInlineImageSpans = (source, references = {}) => {
  const env = { references, unsupportedImageSpans: [] };
  try {
    // Call the inline parser directly instead of parseInline. MarkdownIt's
    // core normalize rule rewrites CRLF/CR line endings before tokenization,
    // which would make the collected offsets point at a different source
    // string and leave valid images in Windows-authored drafts untouched.
    const tokens = [];
    imageContextTokenizer.inline.parse(
      source,
      imageContextTokenizer,
      env,
      tokens
    );
  } catch {
    return [];
  }

  // Nested image labels are parsed using a separate inline source. Keep only
  // spans belonging to this pass; nested labels are normalized recursively.
  return env.unsupportedImageSpans.filter(span => span.source === source);
};

const getOutermostImageSpans = spans => {
  const ordered = [...spans].sort(
    (left, right) => left.start - right.start || right.end - left.end
  );
  const outermost = [];

  ordered.forEach(span => {
    const isNested = outermost.some(
      parent =>
        parent.start <= span.start &&
        parent.end >= span.end &&
        (parent.start < span.start || parent.end > span.end)
    );
    if (!isNested) outermost.push(span);
  });

  return outermost;
};

const MAX_IMAGE_NORMALIZATION_DEPTH = 20;

const stripUnsupportedImageSyntaxInternal = (
  content,
  references = {},
  depth = 0,
  maskCodeBlocks = true
) => {
  const source = String(content ?? '');
  if (!source.includes('![')) return source;

  let inlineSource = source;
  let inheritedReferences = references;

  if (maskCodeBlocks) {
    const context = getProtectedMarkdownRanges(source, imageContextTokenizer);
    inlineSource = maskReferenceDefinitionLines(
      maskRanges(source, context.ranges)
    );
    inheritedReferences = {
      ...context.references,
      ...references,
    };
  }

  const spans = getOutermostImageSpans(
    collectInlineImageSpans(inlineSource, inheritedReferences)
  );
  if (!spans.length) return source;

  return spans
    .sort((left, right) => right.start - left.start)
    .reduce((result, span) => {
      const replacement =
        depth < MAX_IMAGE_NORMALIZATION_DEPTH
          ? stripUnsupportedImageSyntaxInternal(
              span.replacement,
              inheritedReferences,
              depth + 1,
              false
            )
          : span.replacement;
      return `${result.slice(0, span.start)}${replacement}${result.slice(
        span.end
      )}`;
    }, source);
};

/**
 * Apply a Markdown source transform while preserving parsed code blocks.
 *
 * @param {string} content Markdown source
 * @param {(value: string) => string} transform transform for non-code ranges
 * @returns {string} transformed Markdown source
 */
export const transformOutsideMarkdownCodeBlocks = (content, transform) => {
  const original = String(content ?? '');
  const source = normalizeMarkdownLineEndings(original);
  const { ranges } = getProtectedMarkdownRanges(source, imageContextTokenizer);
  if (!ranges.length)
    return restoreMarkdownLineEndings(transform(source), original);

  let cursor = 0;
  let result = '';
  ranges.forEach(({ start, end }) => {
    result += transform(source.slice(cursor, start));
    result += source.slice(start, end);
    cursor = end;
  });
  result += transform(source.slice(cursor));
  return restoreMarkdownLineEndings(result, original);
};

/**
 * Replace unsupported Markdown image syntax with its alt text.
 *
 * The message editor's Context::Default schema does not contain an image
 * node. Reference/shortcut images are recognized using MarkdownIt's reference
 * definitions, while fenced and indented code blocks stay byte-for-byte
 * intact. Closed inline code spans are protected by the scanner; an unclosed
 * backtick is treated as ordinary text so images after it cannot crash the
 * ProseMirror parser.
 *
 * @param {string} content Markdown source
 * @returns {string} source with unsupported image wrappers removed
 */
export const stripUnsupportedImageSyntax = content => {
  const original = String(content ?? '');
  const normalized = normalizeMarkdownLineEndings(original);
  return restoreMarkdownLineEndings(
    stripUnsupportedImageSyntaxInternal(normalized),
    original
  );
};

/**
 * Markdown formatting patterns for stripping unsupported formatting.
 *
 * Maps camelCase type names to ProseMirror snake_case schema names.
 * Order matters: codeBlock before code to avoid partial matches.
 */
export const MARKDOWN_PATTERNS = [
  // --- BLOCK NODES ---
  {
    type: 'codeBlock', // PM: code_block, eg: ```js\ncode\n```
    patterns: [
      { pattern: /`{3}(?:\w+)?\n?([\s\S]*?)`{3}/g, replacement: '$1' },
    ],
  },
  {
    type: 'blockquote', // PM: blockquote, eg: > quote
    patterns: [{ pattern: /^> ?/gm, replacement: '' }],
  },
  {
    type: 'bulletList', // PM: bullet_list, eg: - item
    patterns: [{ pattern: /^[\t ]*[-*+]\s+/gm, replacement: '' }],
  },
  {
    type: 'orderedList', // PM: ordered_list, eg: 1. item
    patterns: [{ pattern: /^[\t ]*\d+\.\s+/gm, replacement: '' }],
  },
  {
    type: 'heading', // PM: heading, eg: ## Heading
    patterns: [{ pattern: /^#{1,6}\s+/gm, replacement: '' }],
  },
  {
    type: 'horizontalRule', // PM: horizontal_rule, eg: ---
    patterns: [{ pattern: /^(?:---|___|\*\*\*)\s*$/gm, replacement: '' }],
  },
  {
    type: 'image', // PM: image, eg: ![alt](url)
    patterns: [
      { pattern: /!\[([^\]]*)\]\([^)]+\)/g, replacement: '$1' },
      { pattern: /!\[([^\]]*)\]\[[^\]]*\]/g, replacement: '$1' },
    ],
  },
  {
    type: 'hardBreak', // PM: hard_break, eg: line\\\n or line  \n
    patterns: [
      { pattern: /\\\n/g, replacement: '\n' },
      { pattern: / {2,}\n/g, replacement: '\n' },
    ],
  },
  // --- INLINE MARKS ---
  {
    type: 'strong', // PM: strong, eg: **bold** or __bold__
    patterns: [
      { pattern: /\*\*(.+?)\*\*/g, replacement: '$1' },
      { pattern: /__(.+?)__/g, replacement: '$1' },
    ],
  },
  {
    type: 'em', // PM: em, eg: *italic* or _italic_
    patterns: [
      { pattern: /(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, replacement: '$1' },
      // Match _text_ only at word boundaries (whitespace/string start/end)
      // Preserves underscores in URLs (e.g., https://example.com/path_name) and variable names
      {
        pattern: /(?<=^|[\s])_([^_\s][^_]*[^_\s]|[^_\s])_(?=$|[\s])/g,
        replacement: '$1',
      },
    ],
  },
  {
    type: 'strike', // PM: strike, eg: ~~strikethrough~~
    patterns: [{ pattern: /~~(.+?)~~/g, replacement: '$1' }],
  },
  {
    type: 'code', // PM: code, eg: `inline code`
    patterns: [{ pattern: /`([^`]+)`/g, replacement: '$1' }],
  },
  {
    type: 'link', // PM: link
    patterns: [
      { pattern: /\[([^\]]+)\]\([^)]+\)/g, replacement: '$1' }, // [text](url) -> text
      { pattern: /<([a-zA-Z][a-zA-Z0-9+.-]*:[^\s>]+)>/g, replacement: '$1' }, // <https://...>, <mailto:...>, <tel:...>, <ftp://...>, etc
      { pattern: /<([^\s@]+@[^\s@>]+)>/g, replacement: '$1' }, // <user@example.com> -> user@example.com
    ],
  },
];

// Editor image resize options for Message Editor
export const MESSAGE_EDITOR_IMAGE_RESIZES = [
  {
    name: 'Small',
    height: '24px',
  },
  {
    name: 'Medium',
    height: '48px',
  },
  {
    name: 'Large',
    height: '72px',
  },
  {
    name: 'Original Size',
    height: 'auto',
  },
];
